import Backdrop from 'components/backdrop';
import Hero from 'components/hero';
import Layout from 'components/layout';
import ModuleRender from 'components/module-render';
import SEO from 'components/seo';
import {
  Heading,
  Image,
  Stack,
  StaggerGrid,
  Text,
  TextBlock,
} from 'components/ui';
import { GetStaticProps, NextPage } from 'next';
import { REVALIDATE_TIME } from 'settings/config';
import { __DEV__ } from 'utils/helpers';
import {
  aboutQuery,
  SharedData,
  sharedDataQuery,
  teamMembersQuery,
} from 'utils/queries';
import {
  getHeroProps,
  urlForImage,
  usePreviewSubscription,
} from 'utils/sanity';
import { getClient } from 'utils/sanity-server';

interface AboutProps {
  pageData?: Sanity.Schema.About;
  teamMembersData?: Sanity.Schema.TeamMember[];
  sharedData?: SharedData;
  preview?: boolean;
}

const About: NextPage<AboutProps> = ({
  pageData,
  teamMembersData,
  sharedData,
  preview,
}) => {
  const { data: page } = usePreviewSubscription(aboutQuery, {
    initialData: pageData,
    enabled: preview,
  });

  const { data: teamMembers } = usePreviewSubscription(teamMembersQuery, {
    initialData: teamMembersData,
    enabled: preview,
  });

  const { data: shared } = usePreviewSubscription(sharedDataQuery, {
    initialData: sharedData,
    enabled: preview,
  });

  return (
    <Layout preview={preview} spaceLinks={shared.spaceInfoPaths}>
      <SEO
        title={page.seo?.title}
        description={page.seo?.description}
        image={page.seo?.ogImage}
      />
      <Hero {...getHeroProps(page.hero)} />
      {page.modules && (
        <ModuleRender modules={page.modules} sharedData={shared} />
      )}
      {teamMembers && (
        <Backdrop>
          <Stack space={4}>
            <Heading level='h2'>Our team</Heading>
            <StaggerGrid>
              {teamMembers.map((teamMember) => (
                <TextBlock key={teamMember._id}>
                  <Stack space={2}>
                    {teamMember.image && (
                      <Image
                        alt={teamMember.image.alt || teamMember.name}
                        src={urlForImage(teamMember.image).url() || ''}
                        width={1024}
                        height={1024}
                        objectFit='cover'
                      />
                    )}
                    <Heading level='h3' variant='sans-serif'>
                      {teamMember.name}
                    </Heading>
                    <Text fontWeight='bold'>{teamMember.position}</Text>
                    <Text>{teamMember.bio}</Text>
                  </Stack>
                </TextBlock>
              ))}
            </StaggerGrid>
          </Stack>
        </Backdrop>
      )}
    </Layout>
  );
};

export default About;

export const getStaticProps: GetStaticProps<AboutProps> = async ({
  preview = __DEV__,
}) => {
  const pageData = await getClient(preview).fetch<Sanity.Schema.About>(
    aboutQuery
  );
  const teamMembersData = await getClient(preview).fetch<
    Sanity.Schema.TeamMember[]
  >(teamMembersQuery);
  const sharedData = await getClient(preview).fetch<SharedData>(
    sharedDataQuery
  );

  return {
    props: {
      pageData,
      teamMembersData,
      sharedData,
      preview,
    },
    revalidate: REVALIDATE_TIME,
  };
};
